import React, { useEffect, useState } from "react";
import AdminLayout from "@/components/layout/AdminLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, X, Trophy, Check } from "lucide-react";

const TIERS = ["national_elite", "provincial_elite", "provincial", "regional", "showcase"];
const PROVINCES = ["", "ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const EMPTY = { name: "", abbreviation: "", province: "", country: "Canada", tier: "provincial_elite", strength_rating: 60, age_divisions: [], active: true, website: "" };

export default function AdminLeagues() {
  const [leagues, setLeagues] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/live/leagues").then(({ data }) => setLeagues(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  return (
    <AdminLayout>
    <div className="max-w-4xl mx-auto p-6" data-testid="admin-leagues">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="label-overline text-[#E63946] flex items-center gap-2"><Trophy className="w-4 h-4" /> CBR Live</div>
          <h1 className="font-display text-3xl uppercase tracking-tight text-[#0A192F]">Leagues</h1>
          <p className="text-sm text-slate-500 mt-1">Leagues feed League Strength into the CBR Team Rating™ algorithm.</p>
        </div>
        <button data-testid="new-league-btn" onClick={() => setEditing({ ...EMPTY })} className="flex items-center gap-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider px-4 py-2 rounded-sm hover:bg-[#c62f3b]">
          <Plus className="w-4 h-4" /> New League
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
            {["League", "Abbr", "Province", "Tier", "Strength", "Active", ""].map((h) => <th key={h} className="text-left px-4 py-2">{h}</th>)}
          </tr></thead>
          <tbody>
            {leagues.map((l) => (
              <tr key={l.id} data-testid={`league-row-${l.id}`} className="border-b border-slate-50 last:border-0">
                <td className="px-4 py-3 font-semibold text-[#0A192F]">{l.name}</td>
                <td className="px-4 py-3 font-data">{l.abbreviation || "—"}</td>
                <td className="px-4 py-3">{l.province || "—"}</td>
                <td className="px-4 py-3 capitalize text-slate-500">{(l.tier || "").replace(/_/g, " ")}</td>
                <td className="px-4 py-3 font-data text-[#0A192F]">{l.strength_rating}</td>
                <td className="px-4 py-3">{l.active ? <Check className="w-4 h-4 text-emerald-500" /> : <span className="text-slate-300">—</span>}</td>
                <td className="px-4 py-3 text-right"><button data-testid={`edit-league-${l.id}`} onClick={() => setEditing(l)} className="text-slate-400 hover:text-[#E63946]"><Pencil className="w-4 h-4" /></button></td>
              </tr>
            ))}
            {leagues.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-slate-400">No leagues yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <LeagueModal league={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
    </AdminLayout>
  );
}

function LeagueModal({ league, onClose, onSaved }) {
  const isNew = !league.id;
  const [f, setF] = useState({ ...EMPTY, ...league, age_divisions: (league.age_divisions || []).join(", ") });
  const [busy, setBusy] = useState(false);
  const save = () => {
    if (!f.name.trim()) return toast.error("Name required");
    setBusy(true);
    const body = { ...f, strength_rating: Number(f.strength_rating), age_divisions: f.age_divisions.split(",").map((s) => s.trim()).filter(Boolean) };
    const req = isNew ? api.post("/live/leagues", body) : api.patch(`/live/leagues/${league.id}`, body);
    req.then(() => { toast.success(isNew ? "League created" : "League updated"); onSaved(); })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed")).finally(() => setBusy(false));
  };
  const inp = "w-full border border-slate-200 rounded-sm px-3 py-2 text-sm";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="league-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-md rounded-lg p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl uppercase tracking-tight text-[#0A192F]">{isNew ? "New League" : "Edit League"}</h3>
          <button onClick={onClose} className="text-slate-400"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input data-testid="league-name" placeholder="League name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="league-abbr" placeholder="Abbreviation" value={f.abbreviation || ""} onChange={(e) => setF({ ...f, abbreviation: e.target.value })} className={inp} />
            <select value={f.province || ""} onChange={(e) => setF({ ...f, province: e.target.value })} className={inp}>{PROVINCES.map((p) => <option key={p} value={p}>{p || "Province…"}</option>)}</select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select data-testid="league-tier" value={f.tier || ""} onChange={(e) => setF({ ...f, tier: e.target.value })} className={inp}>{TIERS.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</select>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-slate-400">Strength (0–100)</label>
              <input data-testid="league-strength" type="number" min="0" max="100" value={f.strength_rating} onChange={(e) => setF({ ...f, strength_rating: e.target.value })} className={inp} />
            </div>
          </div>
          <input placeholder="Age divisions (comma separated, e.g. 18U, 16U)" value={f.age_divisions} onChange={(e) => setF({ ...f, age_divisions: e.target.value })} className={inp} />
          <input placeholder="Website (optional)" value={f.website || ""} onChange={(e) => setF({ ...f, website: e.target.value })} className={inp} />
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active</label>
          <button data-testid="save-league-btn" disabled={busy} onClick={save} className="w-full bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-2.5 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50">{isNew ? "Create League" : "Save Changes"}</button>
        </div>
      </div>
    </div>
  );
}
